
import { VehicleType } from "@enums/VehicleType.enum";
import { ICombinedPreferences } from "@interfaces/ICombinedPreferences";
import { IGoogleMapsMarker, IGoogleMapsMarkerOptions } from "@interfaces/IGoogleMapsMarkerOptions";
import { IPlot, IPlotEvent, IVeriMarkerOptions } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { TranslationService } from "@services/translation.service";
import { ClusterIcon, NgMapMarkerClusterer } from "@typings/google.maps.angular";
import * as angular from "angular";
import { SettingsName } from "../class/SettingsName";
import { SessionService } from "./session.service";
import { VeriDate } from "./veriDate.service";
import { VeriMarkerService } from "./veriMarker.service";

export enum MarkerAnchorPosition {
    Left,
    Top,
    Right,
    Bottom
}

export enum EventMarkerType {
    CurrentPosition,
    Start,
    Stop
}

export interface ICreateEventMarkerOptions {
    label: string;
    markerClass?: string;
    subIcon?: string;
    showLabel?: boolean;
    imageUrl?: string;
    anchorPosition?: MarkerAnchorPosition /*= MarkerAnchorPosition.Top*/;
    colour?: string /*= null*/;
    position?: google.maps.LatLngLiteral;
    eventType?: EventMarkerType;
}

export interface LabeledMarkerOptions<T> {
    label: string;
    id: number;
    lat: number;
    lng: number;
    icon?: string;
    model?: T;
}

export class GoogleMarkerService {

    private markerWidth = 0;
    private markerHeight = 0;

    static $inject = ["$q", "veriDate", "veriMarkerService", "sessionService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly veriDate: VeriDate,
        private readonly veriMarkerService: VeriMarkerService,
        private readonly factSession: SessionService,
        private readonly translationService: TranslationService    ) {
        this.markerWidth = this.veriMarkerService.getDefaultMarkerWidth();
        this.markerHeight = this.veriMarkerService.getDefaultMarkerWidth();
    }

    generateMarkerPipOptions(plot: IPlot): IGoogleMapsMarkerOptions {

        let options: IGoogleMapsMarkerOptions = {
            isPip: true
        };

        const width = 12;

        const icon: google.maps.Icon = {
            url: this.veriMarkerService.generatePip(plot.Reason, width),
            scaledSize: new google.maps.Size(width, width),
            anchor: new google.maps.Point(width / 2, width / 2)
        }

        options = {
            icon: icon,
            zIndex: 1000,
            title: this.veriDate.formatDate(plot.UtcMoment, "fulltime")
        };

        return options;
    }

    generateGoogleMapsMarkerOptionsForPlot(plot: IPlot, selected?: boolean): angular.IPromise<IGoogleMapsMarkerOptions> {
        this.veriMarkerService.createVeriMarkerOptions(plot, selected);

        return this.generateMarkerOptions(plot, plot.veriMarkerOptions);
    }

    generateGoogleMapsMarkerOptionsForVehicle(vehicle: IVehicle, selected?: boolean, showPlotTime: boolean = false): angular.IPromise<IGoogleMapsMarkerOptions> {
        if (this.isMarkerOptionsSame(vehicle.Plot, selected)) {
            return this.$q.when(vehicle.Plot.markerModel.options);
        }

        const userOptions = this.factSession.getItem<ICombinedPreferences>(SettingsName.UserOptions);

        this.veriMarkerService.createVeriMarkerOptions(vehicle.Plot, selected);

        if (userOptions.VehicleLabel.Value) {
            if (userOptions.MarkerLabel.Value === 'Vehicle') {
                if (userOptions.VehicleLabelText.Value == 'Registration') {
                    vehicle.Plot.veriMarkerOptions.label = vehicle.ShortReg;
                }

                else if (userOptions.VehicleLabelText.Value === 'Label') {
                    vehicle.Plot.veriMarkerOptions.label = vehicle.Reg;
                }

                vehicle.Plot.veriMarkerOptions.labelIsReg = vehicle.VehicleType != VehicleType.Trailer;
            }

            else if (userOptions.MarkerLabel.Value === 'Driver') {
                vehicle.Plot.veriMarkerOptions.label = vehicle.Driver ? vehicle.Driver.Name : this.translationService.t('googleMarkerService.noDriver');
            }
        }

        var optionsCopy = angular.copy(vehicle.Plot.veriMarkerOptions);

        return this.generateMarkerOptions(vehicle.Plot, vehicle.Plot.veriMarkerOptions).then(googleMarkerOptions => {

            if (optionsCopy.label) {
                googleMarkerOptions.labelAnchor = new google.maps.Point(40, -20);
                googleMarkerOptions.labelClass = "vehicle-reg-container";
                googleMarkerOptions.labelContent = `<vehicle-reg class='${optionsCopy.labelIsReg ? null : 'trailer'}'>${optionsCopy.label}</vehicle-reg>`;

                if (vehicle.attachedTrailers) {
                    vehicle.attachedTrailers.forEach(trailer => {
                        googleMarkerOptions.labelContent += `<vehicle-reg class='trailer'>${trailer.Reg}</vehicle-reg>`;
                    });
                }

                if (showPlotTime) {
                    const formattedTime: string = this.veriDate.formatDate(vehicle.Plot.Utc, "time");
                    googleMarkerOptions.labelContent += `<vehicle-plot-time>${formattedTime}</vehicle-plot-time>`;
                }
            }

            return googleMarkerOptions;
        });
    }

    private isMarkerOptionsSame(plot: IPlot, selected: boolean) {
        if (!plot.markerModel) {
            return false;
        }

        return plot.markerModel.options && plot && plot.markerModel.options.plotId == plot.Id && plot.markerModel.options.selected == selected;
    }

    generateMarkerOptions(plot: IPlot, veriMarkerOptions: IVeriMarkerOptions): angular.IPromise<IGoogleMapsMarkerOptions> {

        var defer = this.$q.defer<IGoogleMapsMarkerOptions>();

        if (veriMarkerOptions == null) {
            plot.markerModel.options = null;
            defer.resolve(null);
            return defer.promise;
        }

        if (this.isMarkerOptionsSame(plot, veriMarkerOptions.selected)) {
            // May not be as important now we cache images
            defer.resolve(plot.markerModel.options);
            return defer.promise;
        }

        return this.veriMarkerService.generateMarkerImageUrl(veriMarkerOptions).then(url => {

            const icon: google.maps.Icon = {
                url: url,
                anchor: new google.maps.Point(this.markerWidth / 2, this.markerWidth / 2),
                scaledSize: new google.maps.Size(veriMarkerOptions.width || this.markerWidth, veriMarkerOptions.height || this.markerHeight)
            };

            const options: IGoogleMapsMarkerOptions = {
                plotId: plot.Id,
                selected: veriMarkerOptions.selected,
                icon: icon,
                draggable: false,
                visible: true,
                zIndex: 1000,
                position: { lat: plot.Latitude, lng: plot.Longitude }
            };

            if (veriMarkerOptions.time) {
                options.title = this.veriDate.formatDate(veriMarkerOptions.time, "fulltime");
            }

            if (!plot.markerModel) {
                plot.markerModel = {
                    id: plot.Id,
                    options: options
                };
            } else {
                plot.markerModel.options = options;
            }

            return plot.markerModel.options;
        });
    }

    createLabeledMarker<T = any>(options: LabeledMarkerOptions<T>): IGoogleMapsMarker<T> {
        const anchorPosition = this.getEventMarkerAnchorPosition();

        const markerOptions: ICreateEventMarkerOptions = {
            label: options.label,
            showLabel: true,
            anchorPosition,
            position: { lat: options.lat, lng: options.lng },
            subIcon: options.icon
        }

        const googleMarkerOptions = this.createEventMarkerOptions(markerOptions)

        return { 
            id: options.id, 
            options: googleMarkerOptions, 
            model: options.model 
        };
    }

    getEventMarkerAnchorPosition(): MarkerAnchorPosition {
        return MarkerAnchorPosition.Top;
    }

    createEventMarker(plot: IPlot, options: ICreateEventMarkerOptions, data: any = null): IPlotEvent {
        const event: IPlotEvent = {
            plot: plot,
            label: options.label,
            id: `${plot.Id}-${plot.Utc}`,
            Utc: plot.Utc,
            icon: options.subIcon,
            data: data,
            options: null,
            eventType: options.eventType,
            buildOptions: () => {
                return this.createEventMarkerOptions(options);
            }
        }

        options.position = { lat: plot.Latitude, lng: plot.Longitude };
        event.options = event.buildOptions();

        return event;
    }

    createEventMarkerOptions(options: ICreateEventMarkerOptions): IGoogleMapsMarkerOptions {

        let showLabelClass = !options.showLabel ? "hide-label" : "";
        const style = options.colour ? `style='--color: ${options.colour}'` : "";
        let labelContent = `<div class='${options.markerClass} ${showLabelClass}' ${style}>`;

        if (options.subIcon) {
            labelContent += `<i class='material-icons marker-sub-icon'>${options.subIcon}</i>`;
        }

        labelContent += `<div class="marker-contents">`;
        labelContent += `<div class="marker-label">${options.label}</div>`;
        if (options.imageUrl) {
            labelContent += `<img src="${options.imageUrl}" />`;
        }

        labelContent += `</div>`;

        labelContent += "</div>";

        if (options.anchorPosition == undefined) {
            options.anchorPosition = MarkerAnchorPosition.Top;
        }

        let labelClass = `marker-event-2 anchor-${MarkerAnchorPosition[options.anchorPosition].toLocaleLowerCase()}`;

        const markerOptions: IGoogleMapsMarkerOptions = {
            fillColor: "red",
            icon: "//maps.gstatic.com/mapfiles/transparent.png",
            labelClass: labelClass,
            labelContent: labelContent,
            position: options.position
        }

        return markerOptions;
    }

    createEventTooltipTable(label: string, plot?: IPlot): string {
        if (!plot || !plot.UtcMoment) {
            return label;
        }

        var plotText = `<div class="footer">
                                <span>${label}</span>
                                <span class="reason-code-status-indicator rc-${plot.Reason}-background"></span>
                                <span>${this.veriDate.formatDate(plot.UtcMoment)}</span>
                            </div>`;

        return plotText;
    }

    addCustomClustering(ngMapMarkerClusterer: NgMapMarkerClusterer, showMethod: (clusterIcon: ClusterIcon) => void) {
        console.warn("addCustomClustering is deprecated, please use postClusterShow");

        ngMapMarkerClusterer.clusters_.forEach(c => {

            this.updateClusterIconFunction(c.clusterIcon_, showMethod);
        });
    }

    // ---

    updateClusterIconFunction(cluster: ClusterIcon, showMethod: (clusterIcon: ClusterIcon) => void) {

        if (cluster._show) {
            return;
        }

        // Extend our prototype here
        cluster._show = cluster.show;
        cluster.show = function () {
            const clusterIcon: ClusterIcon = this;
            clusterIcon._show();

            showMethod(clusterIcon);
        }
    }
}
